'use client';

import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { useToastsStore } from '@/components/base/toast/use-toast';
import useAnimatedMount from '@/hooks/use-animated-mount';
import { useBreakpointUp } from '@/hooks/use-theme';
import {
  CheckCircle,
  Info,
  Warning,
  XCircle,
} from '@phosphor-icons/react/dist/ssr';
import { clsx } from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import IconButton from '../button/icon-button';

export interface ToastProps {
  type: 'error' | 'success' | 'info';
  content: string;
  id: string;
  duration?: number;
  isDismissible?: boolean;
}

const baseClasses = clsx(
  'flex items-start rounded-m border-3 bg-main-transparent backdrop-blur',
);

const typeClasses = {
  error: clsx('border-error-muted'),
  success: clsx('border-success'),
  info: clsx('border-neutral'),
};

const iconMap = {
  error: Warning,
  success: CheckCircle,
  info: Info,
};

const iconColorMap = {
  error: clsx('text-error'),
  success: clsx('text-success'),
  info: clsx('text-info'),
};

const animationOptions = {
  beforeEntry: clsx('-translate-y-full opacity-0'),
  entry: 'anim-move-in',
  beforeExit: 'anim-move-out',
  exit: clsx('anim-move-out -translate-y-full opacity-0'),
} as const;

const Toast = ({
  type,
  content,
  id,
  duration = 5000,
  isDismissible = type !== 'success',
}: ToastProps) => {
  const removeToast = useToastsStore(({ removeToast }) => removeToast);
  const isDesktop = useBreakpointUp('s');

  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const { ref, mounted, className } = useAnimatedMount<HTMLDivElement>(
    isOpen,
    animationOptions,
  );

  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    if (mounted) {
      setHasMounted(true);
    }
  }, [mounted]);

  useEffect(() => {
    if (!mounted && hasMounted) {
      removeToast(id);
    }
  }, [mounted, hasMounted, removeToast, id]);

  useEffect(() => {
    setTimeout(() => setIsOpen(true));
    setTimeout(handleClose, duration);
  }, []);

  return (
    <div
      className={clsx(
        baseClasses,
        typeClasses[type],
        className,
        isDesktop && 'mr-m',
      )}
      onClick={handleClose}
      ref={ref}
    >
      <div
        className={clsx(
          'flex w-full min-w-[308px] max-w-max-body-text-width flex-1 items-start gap-xxs py-xs pl-xs',
          !isDismissible && 'pr-xs',
        )}
      >
        <PhosphorIcon
          Icon={iconMap[type]}
          className={clsx('icon-m shrink-0', iconColorMap[type])}
          variant="fill"
        />
        <div className="flex-1 text-body text-default">{content}</div>
      </div>
      {isDismissible && <IconButton Icon={XCircle} onClick={handleClose} />}
    </div>
  );
};

export default Toast;
